import { formatCurrency, formatDate } from "@angular/common";
import { AbstractControl, FormControl, Validators } from "@angular/forms";
import { ActivatedRoute, Data } from "@angular/router";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import dayjs from "dayjs";
import { TransactionTypeEnum } from "projects/assist/src/app/accounting/models/transaction";
import { Federations } from "projects/assist/src/app/organisation/models/federation";
import { Observable, map } from "rxjs";
import { Moment } from "../../../../../../../mijn/src/app/member/models/moment";
import { ButtonTypeEnum } from "../../../form/components/button/button.component";
import { BasicDto } from "../../../models/basicDto";
import { WorkingYearType } from "../../../models/workingYearDto";
import { SelectOption } from "../../../models/option";
import { RightNavItem } from "../../../models/rightnav";
import { PluralEnum } from "../../../pipes/to-name.pipe";
import { GlobalVariables } from "../../../services/global.service";
import { AlertModalComponent } from "../../alert-modal/alert-modal.component";
import { ConfirmModalComponent } from "../../confirm-modal/confirm-modal.component";

import { HotToastService } from "@ngneat/hot-toast";
import { SubscriptionTypeEnum } from "projects/assist/src/app/organisation/models/subscription";
import { BeaconService } from "../../../services/beacon.service";
import { environment } from "projects/assist/src/environments/environment";
export class BasePage {
  protected plural: PluralEnum = PluralEnum.plural;
  protected single: PluralEnum = PluralEnum.single;
  protected global = GlobalVariables;
  protected federations = Federations;

  protected subscriptionTypes: number[] | null = null;
  protected allowedFederations: Federations[] | null = null;
  public isLoading: boolean = true;
  protected fragment: string | null = null;
  protected sidenav: RightNavItem[] = [];
  protected customNav: boolean = false;

  protected routeData?: Data;

  protected TransactionTypeEnum = TransactionTypeEnum;
  protected SubscriptionTypeEnum = SubscriptionTypeEnum;
  protected WorkingYearType = WorkingYearType;
  protected env = environment;

  constructor(
    protected route?: ActivatedRoute,
    protected modal?: NgbModal,
    protected toast?: HotToastService,
    protected beacon?: BeaconService
  ) {
    this.route?.data.subscribe((data) => {
      this.routeData = data;
      if (data["subscriptionTypes"]) this.subscriptionTypes = data["subscriptionTypes"];
      if (data["federations"]) this.allowedFederations = data["federations"];

      this.beacon?.searchDocs(data["title"] || "");
    });
  }

  ButtonType = ButtonTypeEnum;

  confirm(
    title: string,
    message: string,
    confirmText: string = "Ja, ga verder",
    cancelText: string = "Annuleren",
    confirmSecondaryText?: string,
    confirmSecondaryColor?: string,
    size: "sm" | "md" | "lg" | "xl" = "md",
    onConfirm?: (modalRef: NgbModalRef) => void
  ): Promise<any> {
    if (this.modal) {
      const modal = this.modal.open(ConfirmModalComponent, {
        size: size,
        centered: true,
      });
      const component: ConfirmModalComponent = modal.componentInstance;
      component.title = title;
      component.text = message;
      component.confirmText = confirmText;
      component.cancelText = cancelText;
      if (onConfirm) {
        component.confirm.subscribe(() => onConfirm(modal));
      }
      if (confirmSecondaryText) {
        component.confirmSecondaryText = confirmSecondaryText;
      }
      if (confirmSecondaryColor) {
        component.confirmSecondaryColor = confirmSecondaryColor;
      }
      return modal.result;
    } else {
      return new Promise(() => confirm(message));
    }
  }

  alert(
    title: string,
    message: string,
    confirmText: string = "Venster sluiten",
    size: "sm" | "md" | "lg" | "xl" = "md"
  ): Promise<any> | undefined {
    if (this.modal) {
      let modal = this.modal.open(AlertModalComponent, {
        size: size,
        centered: true,
      });
      modal.componentInstance.title = title;
      modal.componentInstance.text = message;
      modal.componentInstance.confirmText = confirmText;
      return modal.result;
    } else {
      alert(message);
      return undefined;
    }
  }

  //Todo: move to FormControl
  setRequired(control: FormControl, isRequired: boolean = true) {
    if (isRequired) control.addValidators(Validators.required);
    else control.removeValidators(Validators.required);
    control.updateValueAndValidity();
  }

  scrollToTop() {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }

  scrollTo(id: string) {
    const element = document.getElementById(id);
    if (element)
      window.scroll({
        top: this.getTopOffset(element),
        left: 0,
        behavior: "smooth",
      });
    //if (element)
    //  element.scrollTo({ behavior: "smooth" });
  }

  focus(id: string, selectAll: boolean = true, timeout: number = 100) {
    setTimeout(() => {
      document.getElementById(id)?.focus();
      if (selectAll) (document.getElementById(id) as HTMLInputElement)?.select();
    }, timeout);
  }

  addLeadingZeros(number: number, zeros: string) {
    let strNumber = "" + number;
    return zeros.substring(0, zeros.length - strNumber.length) + strNumber;
  }
  protected getTopOffset(controlEl: HTMLElement): number {
    const labelOffset = 50;
    return controlEl.getBoundingClientRect().top + window.scrollY - labelOffset;
  }

  formatMoney(amount: number | null | undefined) {
    if (!amount) amount = 0;
    return formatCurrency(amount, "nl-BE", "");
  }

  formatDateOnly(date: Date | null | undefined, emptyValue = "") {
    if (date) return formatDate(date, "dd/MM/yyyy", "nl-BE");
    return emptyValue;
  }

  formatDateTimeForMoment(date: Moment) {
    if (dayjs(date.StartDateTime).isSame(dayjs(date.EndDateTime), "day")) {
      let dateTimeString = `Op ${dayjs(date.StartDateTime).format("DD/MM/YYYY")} van ${dayjs(date.StartDateTime).format("HH:mm")} tot ${dayjs(date.EndDateTime).format("HH:mm")}`;
      if (date.Address && date.Address.Name) {
        dateTimeString += ` op locatie ${date.Address.Name} (${date.Address.Street}${date.Address.Nr ? " " + date.Address.Nr : ""}, ${date.Address.PostCode} ${date.Address.Location})`;
      }
      return dateTimeString;
    }
    let dateTimeString = `Van ${dayjs(date.StartDateTime).format("DD/MM/YYYY HH:mm")} tot ${dayjs(date.EndDateTime).format("DD/MM/YYYY HH:mm")}`;
    if (date.Address && date.Address.Name) {
      dateTimeString += ` op locatie ${date.Address.Name} (${date.Address.Street}${date.Address.Nr ? " " + date.Address.Nr : ""}, ${date.Address.PostCode} ${date.Address.Location})`;
    }
    return dateTimeString;
  }

  isRequired(formControl: AbstractControl) {
    return formControl.hasValidator(Validators.required);
  }

  hasAccess() {
    if (
      this.allowedFederations &&
      this.allowedFederations.some((x) => x == this.global.organization.FederationId)
    )
      return true;
    if (this.global.subscription && !this.global.subscription.IsActive)
      return (
        this.subscriptionTypes != null &&
        this.subscriptionTypes.some(
          (subscriptionType) => subscriptionType == SubscriptionTypeEnum.FREE
        )
      );
    if (this.global.subscription.SubscriptionTypeId)
      return (
        this.subscriptionTypes == null ||
        this.subscriptionTypes.indexOf(this.global.subscription.SubscriptionTypeId) > -1
      );
    else return false;
  }

  toSelectOptions(
    items: Observable<any[]>,
    idField = "Id",
    nameField = "Name"
  ): Observable<SelectOption[]> {
    return items.pipe(map((items) => items.map((x) => ({ id: x[idField], name: x[nameField] }))));
  }

  observableToSelectOptions(obs: Observable<BasicDto<number>[]>): Observable<SelectOption[]> {
    return obs.pipe(
      map((items) => items.map((item) => ({ id: item.Id, name: item.Name }) as SelectOption))
    );
  }

  goBack() {
    history.back();
  }
  downloadBlob(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
  }

  setRightNav() {
    if (!this.customNav)
      setTimeout(() => {
        this.sidenav = Array.from(document.querySelectorAll(".card"))
          .filter((card) => card.getAttribute("data-sidenav-label"))
          .map((card) => ({
            id: card.getAttribute("id") || "",
            label: card.getAttribute("data-sidenav-label")?.valueOf() || "",
          }));
        if (this.fragment) {
          this.scrollTo(this.fragment);
        }
      }, 250);
  }

  copyToClipboard(text: string): void {
    // Controleer of de moderne Clipboard API beschikbaar is
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          if (this.toast) this.toast.success("'" + text + "' gekopieerd!");
        })
        .catch((err) => {
          console.error("Fout bij het kopi�ren naar klembord:", err);
          this.toast?.error("Fout bij het kopi�ren naar klembord.");
        });
    } else {
      // Valt terug op een oudere methode (voor oudere browsers)
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed"; // voorkomt scrollen
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand("copy");
        if (this.toast) this.toast.success("'" + text + "' gekopieerd!");
      } catch (err) {
        console.error("Fallback: kopi�ren mislukt", err);
        this.toast?.error("Fout bij het kopi�ren naar klembord.");
      }
      document.body.removeChild(textarea);
    }
  }
}
